/**
 * ChangelogPanel
 * Panel que muestra el historial de versiones y cambios de la aplicación.
 */
import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  NewReleases as NewReleasesIcon,
  Security as SecurityIcon,
  AutoAwesome as FeatureIcon,
  BugReport as BugIcon,
  Speed as ImprovementIcon,
  Settings as ConfigIcon,
  Description as DocsIcon,
  Autorenew as RefactorIcon,
  Warning as DeprecatedIcon,
} from "@mui/icons-material";
import {
  changelog,
  APP_VERSION,
  CHANGE_CATEGORIES,
} from "../../../config/changelog";

// Iconos por categoría
const CATEGORY_ICONS = {
  security: <SecurityIcon />,
  feature: <FeatureIcon />,
  fix: <BugIcon />,
  improvement: <ImprovementIcon />,
  config: <ConfigIcon />,
  docs: <DocsIcon />,
  refactor: <RefactorIcon />,
  deprecated: <DeprecatedIcon />,
};

// Colores por tipo de versión
const VERSION_TYPE_COLORS = {
  major: "error",
  minor: "primary",
  patch: "success",
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const VersionBadge = ({ version, type }) => (
  <Chip
    label={`v${version}`}
    color={VERSION_TYPE_COLORS[type] || "default"}
    size="medium"
    sx={{ fontWeight: "bold", fontSize: "1rem" }}
  />
);

const ChangeItem = ({ change }) => {
  const category = CHANGE_CATEGORIES[change.category] || CHANGE_CATEGORIES.feature;

  return (
    <ListItem alignItems="flex-start" sx={{ py: 1 }}>
      <ListItemIcon sx={{ color: category.color, minWidth: 40 }}>
        {CATEGORY_ICONS[change.category] || <FeatureIcon />}
      </ListItemIcon>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body1" fontWeight="medium">
              {change.description}
            </Typography>
            <Chip
              label={category.label}
              size="small"
              sx={{
                bgcolor: category.color,
                color: "white",
                fontSize: "0.7rem",
                height: 20,
              }}
            />
          </Stack>
        }
        secondary={
          change.details && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {change.details}
            </Typography>
          )
        }
      />
    </ListItem>
  );
};

const ReleaseAccordion = ({ release, defaultExpanded = false }) => {
  const isLatest = release.version === APP_VERSION;

  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: isLatest ? "primary.50" : "inherit",
          "&:hover": { bgcolor: isLatest ? "primary.100" : "action.hover" },
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <VersionBadge version={release.version} type={release.type} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {release.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(release.date)}
            </Typography>
          </Box>
          {isLatest && (
            <Chip
              icon={<NewReleasesIcon />}
              label="Actual"
              color="success"
              size="small"
            />
          )}
          <Chip
            label={`${release.changes.length} cambios`}
            size="small"
            variant="outlined"
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <List dense>
          {release.changes.map((change, index) => (
            <ChangeItem key={index} change={change} />
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

const ChangelogPanel = () => {
  const latestRelease = changelog[0];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header con versión actual */}
      <Paper sx={{ p: 2, m: 2, bgcolor: "primary.main", color: "white" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight="bold">
              MCM Visual
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Healthcare Management System
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="h4" fontWeight="bold">
              v{APP_VERSION}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {formatDate(latestRelease.date)}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Resumen de última versión */}
      <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
        <Typography variant="body2">
          <strong>Ultima actualizacion:</strong> {latestRelease.title} -{" "}
          {latestRelease.changes.length} cambios implementados
        </Typography>
      </Alert>

      {/* Lista de versiones */}
      <Box sx={{ flex: 1, overflow: "auto", px: 2, pb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Historial de Versiones
        </Typography>

        {changelog.map((release, index) => (
          <ReleaseAccordion
            key={release.version}
            release={release}
            defaultExpanded={index === 0}
          />
        ))}
      </Box>

      {/* Footer con leyenda */}
      <Paper sx={{ p: 2, mx: 2, mb: 2, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Categorias de cambios:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Object.entries(CHANGE_CATEGORIES).map(([key, cat]) => (
            <Chip
              key={key}
              icon={CATEGORY_ICONS[key]}
              label={cat.label}
              size="small"
              sx={{
                bgcolor: cat.color,
                color: "white",
                "& .MuiChip-icon": { color: "white" },
              }}
            />
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

export default ChangelogPanel;
